import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertCircle, Plus, RefreshCw, Search } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { BusyOverlay } from '@/components/ui/busy-overlay';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { LocalizedName, localizedName } from '@/components/localized-name';
import { useBusinesses } from '@/features/business/use-businesses';
import { formatCurrency, formatNumber, toPercent } from '@/lib/format';
import { cn } from '@/lib/utils';
import { DiscountEditor } from './discount-editor';
import { usePosDiscounts } from './pos-discounts.api';
import {
  DISCOUNT_TYPE,
  emptyDiscount,
  takesValue,
  toInput,
  type PosDiscount,
  type PosDiscountInput,
} from './types';

/**
 * The discounts themselves.
 *
 * `discountValue` is read through the type, and the two that use it do not use
 * it the same way: on a fixed amount it is dollars, on a percentage it is a
 * *fraction* the till multiplies by (0.1 being ten per cent), and on the three
 * tax waivers it means nothing. The original printed the bare number in every
 * case, so a ten per cent discount read as "0.1".
 */
export function DiscountsTab() {
  const { t, i18n } = useTranslation();
  const language = i18n.resolvedLanguage ?? 'en';

  const [draft, setDraft] = useState('');
  const [editing, setEditing] = useState<PosDiscountInput | null>(null);

  const list = usePosDiscounts();
  const { data: businesses } = useBusinesses();

  const discounts = useMemo(() => list.data ?? [], [list.data]);
  // Rows on screen being re-read — the reload button, or a save.
  const busy = list.isFetching && !list.isPending;
  const term = draft.trim().toLowerCase();
  const rows = useMemo(
    () =>
      discounts.filter((discount) =>
        !term
          ? true
          : [discount.nameEn, discount.nameEs]
              .filter(Boolean)
              .some((value) => value!.toLowerCase().includes(term))
      ),
    [discounts, term]
  );

  const valueLabel = (discount: PosDiscount) => {
    if (!takesValue(discount.discountType)) return '—';
    // Stored as a fraction; see `toPercent`.
    return discount.discountType === DISCOUNT_TYPE.percentageAmount
      ? `${formatNumber(toPercent(discount.discountValue))}%`
      : formatCurrency(discount.discountValue);
  };

  const scopeLabel = (discount: PosDiscount) => {
    if (!discount.businessId) return t('posDiscounts.allStores');
    return (
      (businesses ?? []).find((b) => b.id === discount.businessId)?.name ??
      t('posDiscounts.oneStore')
    );
  };

  const excludedCount = (discount: PosDiscount) =>
    discount.excludedItems?.length ?? 0;

  return (
    <div className="space-y-4 pt-4">
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2" />
          <Input
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            placeholder={t('posDiscounts.searchPlaceholder')}
            aria-label={t('posDiscounts.searchPlaceholder')}
            className="h-10 pl-9"
          />
        </div>
        <Button
          variant="outline"
          size="icon"
          className="h-10"
          aria-label={t('common.reload')}
          disabled={list.isFetching}
          onClick={() => void list.refetch()}
        >
          <RefreshCw className={list.isFetching ? 'animate-spin' : ''} />
        </Button>
        <Button className="h-10" onClick={() => setEditing(emptyDiscount())}>
          <Plus />
          <span className="hidden sm:inline">
            {t('posDiscounts.newDiscount')}
          </span>
        </Button>
      </div>

      {list.isError && (
        <div className="border-destructive/30 bg-destructive/8 text-destructive flex items-start gap-2 rounded-xl border p-4 text-sm">
          <AlertCircle className="mt-0.5 size-4 shrink-0" />
          <div className="space-y-2">
            <p>{t('posDiscounts.loadFailed')}</p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => void list.refetch()}
            >
              {t('common.retry')}
            </Button>
          </div>
        </div>
      )}

      {list.isPending && (
        <div className="divide-y rounded-xl border">
          {Array.from({ length: 5 }).map((_, index) => (
            <div key={index} className="flex items-center gap-3 p-3">
              <div className="flex-1 space-y-1.5">
                <Skeleton className="h-4 w-40" />
                <Skeleton className="h-3 w-24" />
              </div>
              <Skeleton className="h-5 w-16" />
            </div>
          ))}
        </div>
      )}

      {list.isSuccess && rows.length === 0 && (
        <div className="relative rounded-xl">
          <BusyOverlay show={busy} />
          <p className="text-muted-foreground rounded-xl border border-dashed py-14 text-center text-sm">
            {discounts.length === 0
              ? t('posDiscounts.none')
              : t('posDiscounts.noneMatch')}
          </p>
        </div>
      )}

      {rows.length > 0 && (
        <>
          <div className="hidden rounded-xl border md:block">
            <Table busy={busy}>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead>{t('posDiscounts.name')}</TableHead>
                  <TableHead>{t('posDiscounts.discountType')}</TableHead>
                  <TableHead className="text-right">
                    {t('posDiscounts.value')}
                  </TableHead>
                  <TableHead>{t('posDiscounts.scope')}</TableHead>
                  <TableHead className="text-right">
                    {t('posDiscounts.exclusions')}
                  </TableHead>
                  <TableHead>{t('posDiscounts.status')}</TableHead>
                  <TableHead className="w-20 text-right" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((discount) => (
                  <TableRow key={discount.id}>
                    <TableCell>
                      <LocalizedName
                        nameEn={discount.nameEn}
                        nameEs={discount.nameEs}
                      />
                    </TableCell>
                    <TableCell className="text-xs">
                      {t(`posDiscounts.type${discount.discountType}`)}
                      {discount.canBeAppliedMultipleTimes && (
                        <p className="text-muted-foreground">
                          {t('posDiscounts.multipleShort')}
                        </p>
                      )}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {valueLabel(discount)}
                    </TableCell>
                    <TableCell className="truncate">
                      {scopeLabel(discount)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {excludedCount(discount) || '—'}
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant={discount.isActive ? 'success' : 'secondary'}
                      >
                        {discount.isActive
                          ? t('posDiscounts.active')
                          : t('posDiscounts.inactive')}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        size="sm"
                        onClick={() => setEditing(toInput(discount))}
                      >
                        {t('posDiscounts.edit')}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          <div className="relative md:hidden">
            <BusyOverlay show={busy} />
            <div
              aria-busy={busy}
              className={cn(
                'space-y-2',
                busy && 'opacity-60 transition-opacity'
              )}
            >
              {rows.map((discount) => (
                <div
                  key={discount.id}
                  className="bg-card space-y-3 rounded-xl border p-3"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="truncate font-medium">
                        {localizedName(discount, language)}
                      </p>
                      <p className="text-muted-foreground truncate text-xs">
                        {t(`posDiscounts.type${discount.discountType}`)} ·{' '}
                        {scopeLabel(discount)}
                      </p>
                    </div>
                    <Badge variant={discount.isActive ? 'success' : 'secondary'}>
                      {discount.isActive
                        ? t('posDiscounts.active')
                        : t('posDiscounts.inactive')}
                    </Badge>
                  </div>
                  <p className="text-sm tabular-nums">{valueLabel(discount)}</p>
                  {excludedCount(discount) > 0 && (
                    <p className="text-muted-foreground text-xs">
                      {t('posDiscounts.exclusionCount', {
                        count: excludedCount(discount),
                      })}
                    </p>
                  )}
                  <Button
                    size="sm"
                    className="w-full"
                    onClick={() => setEditing(toInput(discount))}
                  >
                    {t('posDiscounts.edit')}
                  </Button>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      <DiscountEditor
        discount={editing}
        open={editing !== null}
        onOpenChange={(next) => {
          if (!next) setEditing(null);
        }}
      />
    </div>
  );
}
